import { Card } from '../design-system/Card';
import { Text } from '../design-system/Text';
import { Button } from '../design-system/Button';
import { useNudgeImpression } from '../../hooks/useNudgeImpression';
import { analytics } from '../../lib/analytics';
import { Wallet, X } from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import type { FastagProfile } from '../../types';

interface NudgeWhitespaceCardProps {
  profile: FastagProfile;
  visible: boolean;
  onRecharge: () => void;
  onDismiss: () => void;
  dismissed: boolean;
}

export function NudgeWhitespaceCard({
  profile,
  visible,
  onRecharge,
  onDismiss,
  dismissed,
}: NudgeWhitespaceCardProps) {
  useNudgeImpression('nudge_whitespace_card', visible && !dismissed, { balance: profile.balance });

  if (!visible || dismissed) return null;

  const handleRecharge = () => {
    analytics.emit('recharge_cta_click', { nudge_id: 'nudge_whitespace_card', balance: profile.balance });
    onRecharge();
  };

  const handleDismiss = () => {
    analytics.emit('nudge_dismiss', { nudge_id: 'nudge_whitespace_card' });
    onDismiss();
  };

  return (
    <Card
      padding="md"
      elevation={1}
      className="mx-4 border border-dashed border-slate-300 bg-slate-50/50 dark:border-slate-600 dark:bg-slate-800/50"
      data-testid="nudge-whitespace-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-park-50 text-park-600 dark:bg-park-900/30 dark:text-park-400">
          <Wallet className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Text variant="body-medium">Keep FASTag ready</Text>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss whitespace nudge"
              className="shrink-0 rounded-md p-1 text-slate-400 hover:text-slate-600 focus-visible:ring-2 touch-target"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Text variant="caption" color="secondary" className="mt-1">
            Balance is {formatCurrency(profile.balance)}. Add {formatCurrency(profile.recommendedAmount)} so your next toll is uninterrupted.
          </Text>
          <Button size="sm" variant="outline" onClick={handleRecharge} className="mt-3">
            Recharge {formatCurrency(profile.recommendedAmount)}
          </Button>
        </div>
      </div>
    </Card>
  );
}
